import { type Access, can, type StaffSession } from './access'

type Permission = StaffSession['permissions'][number]

/** The screens the menu opens. */
export type NavPath =
  | '/'
  | '/timeline'
  | '/prezzi'
  | '/bookings'
  | '/kit'
  | '/vehicles'
  | '/modelli'
  | '/customers'
  | '/maintenance'
  | '/cash'
  | '/fiscal'
  | '/fines'
  | '/reports'
  | '/reconciliation'
  | '/people'
  | '/devices'
  | '/terms'
  | '/system'

/** A word under `nav` in the staff messages. */
export type NavLabel =
  | 'today'
  | 'timeline'
  | 'prices'
  | 'bookings'
  | 'kit'
  | 'vehicles'
  | 'models'
  | 'customers'
  | 'maintenance'
  | 'cash'
  | 'fiscal'
  | 'fines'
  | 'reports'
  | 'reconcile'
  | 'people'
  | 'devices'
  | 'terms'
  | 'system'
  | 'groupDesk'
  | 'groupFleet'
  | 'groupMoney'
  | 'groupAdmin'

export interface NavItem {
  to: NavPath
  label: NavLabel
  /** The same check as the screen's own guard and the server's. */
  permission: Permission
}

export interface NavGroup {
  label: NavLabel
  items: NavItem[]
}

/** The menu: four groups, each by the work it holds. */
const GROUPS: readonly NavGroup[] = [
  {
    label: 'groupDesk',
    items: [
      { to: '/', label: 'today', permission: 'today.read' },
      { to: '/timeline', label: 'timeline', permission: 'fleet.read' },
      // W2-S S1: the rate calendar, beside the Planning it reads like (prices.manage, the Owner's).
      { to: '/prezzi', label: 'prices', permission: 'prices.manage' },
      { to: '/bookings', label: 'bookings', permission: 'bookings.read' },
      // S10: the printed fallback kit, the counter's own paper stock (rentals.manage).
      { to: '/kit', label: 'kit', permission: 'rentals.manage' },
    ],
  },
  {
    label: 'groupFleet',
    items: [
      { to: '/vehicles', label: 'vehicles', permission: 'fleet.read' },
      // Q-157: the models standing for each category (models.manage, the Owner's and the Agents').
      { to: '/modelli', label: 'models', permission: 'models.manage' },
      { to: '/customers', label: 'customers', permission: 'customers.read' },
      { to: '/maintenance', label: 'maintenance', permission: 'fleet.manage' },
    ],
  },
  {
    label: 'groupMoney',
    items: [
      { to: '/cash', label: 'cash', permission: 'payments.record' },
      { to: '/fiscal', label: 'fiscal', permission: 'payments.record' },
      { to: '/fines', label: 'fines', permission: 'fines.manage' },
      { to: '/reports', label: 'reports', permission: 'reports.view' },
      { to: '/reconciliation', label: 'reconcile', permission: 'reports.view' },
    ],
  },
  {
    label: 'groupAdmin',
    items: [
      // S9 (ADM-1): the staff accounts, the Owner's (users.manage).
      { to: '/people', label: 'people', permission: 'users.manage' },
      { to: '/devices', label: 'devices', permission: 'devices.manage' },
      // OPS-1: the rental terms' wording, the Owner's (terms.manage).
      { to: '/terms', label: 'terms', permission: 'terms.manage' },
      { to: '/system', label: 'system', permission: 'system.view' },
    ],
  },
]

/**
 * What this person's menu holds: an item only with its permission, and a group
 * only with an item. Hiding a link is not the guard; the screen and the server
 * check again.
 */
export function navGroups(access: Access): NavGroup[] {
  return GROUPS.map((group) => ({
    label: group.label,
    items: group.items.filter((item) => can(access, item.permission)),
  })).filter((group) => group.items.length > 0)
}
